import { useEffect, useRef } from 'react';

interface FiveForcesData {
  competitiveRivalry: { level: string; explanation: string };
  supplierPower: { level: string; explanation: string };
  buyerPower: { level: string; explanation: string };
  threatOfSubstitutes: { level: string; explanation: string };
  barriersToEntry: { level: string; explanation: string };
}

interface FiveForcesRadarChartProps {
  data: FiveForcesData;
  className?: string;
}

export default function FiveForcesRadarChart({ data, className = '' }: FiveForcesRadarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Convert pressure levels to numeric values (inverted: higher = better for company)
  const levelToValue = (level: string): number => {
    switch (level.toLowerCase()) {
      case 'very strong':
        return 1;
      case 'strong':
        return 2;
      case 'moderate':
        return 3;
      case 'weak':
        return 4;
      case 'very weak':
        return 5;
      default:
        return 3;
    }
  };

  // Get color for pressure level
  const levelToColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'very strong':
        return '#dc2626'; // red-600
      case 'strong':
        return '#ea580c'; // orange-600
      case 'moderate':
        return '#d97706'; // amber-600
      case 'weak':
        return '#65a30d'; // lime-600
      case 'very weak':
        return '#16a34a'; // green-600
      default:
        return '#6b7280'; // gray-500
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas dimensions - larger for better label readability
    const size = 550;
    canvas.width = size;
    canvas.height = size;

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 140; // Much more padding for labels

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    const forces = [
      {
        name: 'Competitive\nRivalry',
        value: levelToValue(data.competitiveRivalry?.level || 'Moderate'),
        level: data.competitiveRivalry?.level || 'Moderate',
        color: levelToColor(data.competitiveRivalry?.level || 'Moderate'),
        angle: -Math.PI / 2, // Start at top
      },
      {
        name: 'Supplier\nPower',
        value: levelToValue(data.supplierPower?.level || 'Moderate'),
        level: data.supplierPower?.level || 'Moderate',
        color: levelToColor(data.supplierPower?.level || 'Moderate'),
        angle: -Math.PI / 2 + (2 * Math.PI) / 5,
      },
      {
        name: 'Buyer\nPower',
        value: levelToValue(data.buyerPower?.level || 'Moderate'),
        level: data.buyerPower?.level || 'Moderate',
        color: levelToColor(data.buyerPower?.level || 'Moderate'),
        angle: -Math.PI / 2 + (2 * (2 * Math.PI)) / 5,
      },
      {
        name: 'Threat of\nSubstitutes',
        value: levelToValue(data.threatOfSubstitutes?.level || 'Moderate'),
        level: data.threatOfSubstitutes?.level || 'Moderate',
        color: levelToColor(data.threatOfSubstitutes?.level || 'Moderate'),
        angle: -Math.PI / 2 + (3 * (2 * Math.PI)) / 5,
      },
      {
        name: 'Barriers to\nEntry',
        value: levelToValue(data.barriersToEntry?.level || 'Moderate'),
        level: data.barriersToEntry?.level || 'Moderate',
        color: levelToColor(data.barriersToEntry?.level || 'Moderate'),
        angle: -Math.PI / 2 + (4 * (2 * Math.PI)) / 5,
      },
    ];

    // Draw grid circles with 5 levels (1-5)
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    const gridLevels = 5;
    for (let i = 1; i <= gridLevels; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius * i) / gridLevels, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw grid lines from center to each force
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    forces.forEach((force) => {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(force.angle),
        centerY + radius * Math.sin(force.angle)
      );
      ctx.stroke();
    });

    // Draw category labels with better positioning and readability
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    forces.forEach((force) => {
      const labelDistance = radius + 70;
      const x = centerX + labelDistance * Math.cos(force.angle);
      const y = centerY + labelDistance * Math.sin(force.angle);

      // Handle multi-line labels
      const lines = force.name.split('\n');
      const lineHeight = 16;
      const totalHeight = lines.length * lineHeight;

      // Draw background for better readability
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      const maxLineWidth = Math.max(...lines.map((line) => ctx.measureText(line).width));
      ctx.fillRect(
        x - maxLineWidth / 2 - 10,
        y - totalHeight / 2 - 8,
        maxLineWidth + 20,
        totalHeight + 16
      );

      // Draw border around text background
      ctx.strokeStyle = force.color;
      ctx.lineWidth = 2;
      ctx.strokeRect(
        x - maxLineWidth / 2 - 10,
        y - totalHeight / 2 - 8,
        maxLineWidth + 20,
        totalHeight + 16
      );

      // Draw the text lines
      ctx.fillStyle = force.color;
      lines.forEach((line, index) => {
        const lineY = y + (index - lines.length / 2 + 0.5) * lineHeight;
        ctx.fillText(line, x, lineY);
      });

      // Draw the level below the label
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#374151';
      ctx.fillText(force.level, x, y + totalHeight / 2 + 20);
      ctx.font = 'bold 13px sans-serif'; // Reset font
    });

    // Draw scale indicators at different positions to avoid overlap
    ctx.fillStyle = '#6b7280';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    const scaleLabels = ['Very Strong', 'Strong', 'Moderate', 'Weak', 'Very Weak'];
    for (let i = 1; i <= gridLevels; i++) {
      const labelRadius = (radius * i) / gridLevels;
      // Position scale labels at 36-degree angle (between pentagon points)
      const scaleAngle = Math.PI / 5;
      const x = centerX + labelRadius * Math.cos(scaleAngle) + 5;
      const y = centerY + labelRadius * Math.sin(scaleAngle);

      // Background for scale labels
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      const scaleText = scaleLabels[i - 1];
      const scaleWidth = ctx.measureText(scaleText).width;
      ctx.fillRect(x - 2, y - 8, scaleWidth + 4, 16);

      // Color code the scale labels
      const scaleColor = levelToColor(scaleText);
      ctx.fillStyle = scaleColor;
      ctx.fillText(scaleText, x, y);
    }

    // Draw Five Forces data polygon
    if (forces.some((force) => force.value > 0)) {
      ctx.beginPath();
      forces.forEach((force, index) => {
        const distance = (force.value / 5) * radius; // Scale to 1-5 range
        const x = centerX + distance * Math.cos(force.angle);
        const y = centerY + distance * Math.sin(force.angle);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();

      // Fill polygon with gradient
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(59, 130, 246, 0.3)');
      gradient.addColorStop(1, 'rgba(59, 130, 246, 0.1)');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Stroke polygon
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw data points with labels
      forces.forEach((force) => {
        const distance = (force.value / 5) * radius;
        const x = centerX + distance * Math.cos(force.angle);
        const y = centerY + distance * Math.sin(force.angle);

        // Draw point
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = force.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Draw value label near the point
        if (force.value > 0) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.font = 'bold 11px sans-serif';
          ctx.textAlign = 'center';
          const valueText = force.value.toString();
          const valueWidth = ctx.measureText(valueText).width;

          // Position value label slightly inward from the point
          const labelOffset = 18;
          const labelX = x - labelOffset * Math.cos(force.angle);
          const labelY = y - labelOffset * Math.sin(force.angle);

          ctx.fillRect(labelX - valueWidth / 2 - 4, labelY - 8, valueWidth + 8, 16);
          ctx.fillStyle = force.color;
          ctx.fillText(valueText, labelX, labelY);
        }
      });
    }
  }, [data]);

  const hasData = Object.values(data).some((force) => force.level && force.level !== '');

  if (!hasData) {
    return (
      <div className={`bg-gray-50 rounded-lg p-6 text-center ${className}`}>
        <p className="text-gray-500">No Five Forces data available for radar chart</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg p-4 ${className}`} data-chart-type="five-forces">
      <h4 className="text-lg font-semibold text-gray-900 mb-2 text-center">Five Forces Analysis</h4>
      <div className="flex justify-center mb-4">
        <canvas ref={canvasRef} className="max-w-full" />
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: levelToColor(data.competitiveRivalry.level) }}
            ></div>
            Competitive Rivalry
          </span>
          <span
            className="font-semibold"
            style={{ color: levelToColor(data.competitiveRivalry.level) }}
          >
            {data.competitiveRivalry.level}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: levelToColor(data.supplierPower.level) }}
            ></div>
            Supplier Power
          </span>
          <span className="font-semibold" style={{ color: levelToColor(data.supplierPower.level) }}>
            {data.supplierPower.level}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: levelToColor(data.buyerPower.level) }}
            ></div>
            Buyer Power
          </span>
          <span className="font-semibold" style={{ color: levelToColor(data.buyerPower.level) }}>
            {data.buyerPower.level}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: levelToColor(data.threatOfSubstitutes.level) }}
            ></div>
            Threat of Substitutes
          </span>
          <span
            className="font-semibold"
            style={{ color: levelToColor(data.threatOfSubstitutes.level) }}
          >
            {data.threatOfSubstitutes.level}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: levelToColor(data.barriersToEntry.level) }}
            ></div>
            Barriers to Entry
          </span>
          <span
            className="font-semibold"
            style={{ color: levelToColor(data.barriersToEntry.level) }}
          >
            {data.barriersToEntry.level}
          </span>
        </div>
      </div>

      <div className="mt-4 text-xs text-center text-gray-500">
        <p>
          Outer edge = stronger pressure (worse for company) • Inner area = weaker pressure (better
          for company)
        </p>
      </div>
    </div>
  );
}
